import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Target, BarChart3, Wrench } from 'lucide-react';

const features = [
  {
    icon: Award,
    title: 'Former Google Expert',
    description: 'Direct experience building and optimizing the tools you use daily.',
    color: '#4285F4', // Blue
  },
  {
    icon: Target,
    title: 'Precision Strategy',
    description: 'Every recommendation is backed by platform knowledge and industry best practices.',
    color: '#EA4335', // Red
  },
  {
    icon: BarChart3,
    title: 'Data-Driven Results',
    description: 'We measure everything and optimize continuously for your success.',
    color: '#FBBC05', // Yellow
  },
  {
    icon: Wrench,
    title: 'Full Implementation',
    description: "From strategy to execution, we handle everything so you don't have to.",
    color: '#34A853', // Green
  },
];

export const WhyUs = () => {
  return (
    <section id="about" className="py-20 lg:py-28 bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#4285F4]/3 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Why Choose <span className="text-[#34A853]">MARKETYZE</span>?
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We're not just consultants—we're Google insiders who understand how these platforms work 
            from the inside. Our expertise comes from direct experience at <span className="font-semibold text-foreground">Google</span>, 
            giving us unique insights into optimization strategies that actually work.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
          {features.map((feature, index) => (
            <Card 
              key={feature.title}
              className="bg-card border-border/50 shadow-card hover:shadow-card-hover transition-all duration-300 group service-card"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardContent className="p-6">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="h-7 w-7" style={{ color: feature.color }} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* About Belle Section */}
        <div className="max-w-5xl mx-auto">
          <Card className="bg-gradient-to-br from-secondary/50 to-muted/30 border-border/50 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex flex-col lg:flex-row gap-0 items-stretch">
                {/* Photo */}
                <div className="lg:w-2/5 flex-shrink-0">
                  <img 
                    src="https://cdn.prod.website-files.com/656c80fcdd9dd2937eeff349/67fa20da5cf0810424ce5116_LD_portraits-37.jpg"
                    alt="Belle - Founder of MARKETYZE and former Google Ads specialist"
                    width="600"
                    height="600"
                    loading="lazy"
                    className="w-full h-full object-cover min-h-[300px] lg:min-h-full"
                  />
                </div>
                
                {/* Content */}
                <div className="lg:w-3/5 p-8 lg:p-10 flex flex-col justify-center">
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-4">
                    Meet Belle
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Hi, I'm <span className="font-semibold text-foreground">Belle</span>, and I am the owner of MARKETYZE.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    My time working directly on the Google Ads and Analytics platforms at 
                    <span className="font-semibold text-foreground"> Google </span> 
                    provided more than just experience – it gave me a unique, insider understanding 
                    of how these tools are designed to perform best. I know the strategies, nuances, 
                    and best practices often missed from the outside.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    That's why I created <span className="font-semibold text-foreground">MARKETYZE</span> – 
                    to offer that practical expertise to you.
                  </p>
                  
                  {/* Google Badge */}
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-card shadow flex items-center justify-center border border-border">
                      <img 
                        src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                        alt="Google logo" 
                        width="24"
                        height="8"
                        loading="lazy"
                        className="w-6 h-auto"
                      />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">Former Google Specialist</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
